import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ApprovalActionModal = ({ refund, action, onConfirm, onClose }) => {
  const [refundAmount, setRefundAmount] = useState(refund?.refundAmount || 0);
  const [refundType, setRefundType] = useState('full');
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedReason, setSelectedReason] = useState('');

  const rejectionReasons = [
    'Customer error - service was delivered',
    'Outside refund policy window',
    'Insufficient evidence provided',
    'Duplicate request',
    'Fraudulent activity suspected',
    'Other'
  ];

  const handleConfirm = () => {
    if (action === 'approve') {
      onConfirm({
        amount: refundType === 'full' ? refund?.refundAmount : refundAmount,
        type: refundType,
        notes
      });
    } else {
      onConfirm({
        reason: selectedReason === 'Other' ? rejectionReason : selectedReason
      });
    }
  };

  const isApprove = action === 'approve';
  const canSubmit = isApprove ? (refundType === 'partial' ? refundAmount > 0 : true) : (selectedReason !== '');

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg">
        {/* Header */}
        <div className="bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isApprove ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}>
              <Icon name={isApprove ? 'CheckCircle' : 'XCircle'} size={24} color={isApprove ? '#4ADE80' : '#F87171'} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white font-heading">
                {isApprove ? 'Approve Refund' : 'Reject Refund'}
              </h2>
              <p className="text-sm text-slate-400 font-body">{refund?.requestId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <Icon name="X" size={20} color="#94A3B8" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Refund Summary */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-400 font-caption">Customer</p>
                <p className="text-sm text-white font-medium mt-1 font-body">{refund?.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Original Amount</p>
                <p className="text-sm text-white font-medium mt-1 font-body">₹{refund?.originalAmount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Requested Amount</p>
                <p className="text-sm text-amber-400 font-bold mt-1 font-body">₹{refund?.refundAmount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Reason</p>
                <p className="text-sm text-white font-medium mt-1 font-body">{refund?.reasonCode}</p>
              </div>
            </div>
          </div>

          {isApprove ? (
            <>
              {/* Refund Type */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
                  Refund Type
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-3 p-3 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors">
                    <input
                      type="radio"
                      name="refundType"
                      value="full"
                      checked={refundType === 'full'}
                      onChange={(e) => setRefundType(e?.target?.value)}
                      className="w-4 h-4 text-amber-500 focus:ring-amber-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white font-body">Full Refund</p>
                      <p className="text-xs text-slate-400 font-caption">Refund the complete amount of ₹{refund?.refundAmount?.toLocaleString()}</p>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-3 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors">
                    <input
                      type="radio"
                      name="refundType"
                      value="partial"
                      checked={refundType === 'partial'}
                      onChange={(e) => setRefundType(e?.target?.value)}
                      className="w-4 h-4 text-amber-500 focus:ring-amber-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white font-body">Partial Refund</p>
                      <p className="text-xs text-slate-400 font-caption">Specify a custom refund amount</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Partial Amount Input */}
              {refundType === 'partial' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
                    Refund Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                    <input
                      type="number"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(Number(e?.target?.value))}
                      max={refund?.refundAmount}
                      min={0}
                      className="w-full pl-8 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1 font-caption">Maximum: ₹{refund?.refundAmount?.toLocaleString()}</p>
                </div>
              )}

              {/* Internal Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
                  Internal Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e?.target?.value)}
                  rows={3}
                  placeholder="Add any internal notes about this approval..."
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                />
              </div>
            </>
          ) : (
            <>
              {/* Rejection Reason */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
                  Rejection Reason *
                </label>
                <select
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e?.target?.value)}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Select a reason...</option>
                  {rejectionReasons?.map((reason) => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              {/* Custom Reason */}
              {selectedReason === 'Other' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
                    Detailed Reason *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e?.target?.value)}
                    rows={4}
                    placeholder="Provide detailed reason for rejection..."
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                  />
                </div>
              )}
            </>
          )}

          {/* Warning */}
          <div className={`flex items-start space-x-3 p-3 rounded-lg border ${
            isApprove ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'
          }`}>
            <Icon name="AlertTriangle" size={20} color={isApprove ? '#4ADE80' : '#F87171'} />
            <p className="text-sm text-slate-300 font-body">
              {isApprove
                ? 'This action will process the refund immediately and cannot be undone.'
                : 'This action will reject the refund request and notify the customer.'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-800 border-t border-slate-700 px-6 py-4 flex items-center justify-end space-x-3 rounded-b-xl">
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canSubmit}
            className={isApprove ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}
          >
            <Icon name={isApprove ? 'CheckCircle' : 'XCircle'} size={18} />
            <span className="ml-2">{isApprove ? 'Approve Refund' : 'Reject Request'}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApprovalActionModal;